import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import FRONTEND_CONFIG from "./frontend_config";

export default function MyQuestions() {
  const [questions, setQuestions] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [editedQuestion, setEditedQuestion] = useState({});
  const navigate = useNavigate();

  const [userId, setUserId] = useState(null);

  // ✅ Custom Stylish Alert Popup State
  const [popup, setPopup] = useState({ show: false, message: "" });

  // Function to show popup
  const showAlert = (msg) => {
    setPopup({ show: true, message: msg });
    setTimeout(() => setPopup({ show: false, message: "" }), 2000);
  };
    const [confirmBox, setConfirmBox] = useState({ show: false, id: null });

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem("user"));
    const id = user?._id || user?.id;

    if (!id) {
      showAlert("Please login first.");
      navigate("/login");
      return;
    }

    setUserId(id);
    fetchQuestions(id);
  }, [navigate]);

  const fetchQuestions = async (id) => {
    try {
      const res = await axios.get(
        `${FRONTEND_CONFIG.API_URL}/api/question/my-questions/${id}`
      );
      setQuestions(res.data);
    } catch (err) {
      console.log(err);
      showAlert("❌ Failed to fetch questions");
    }
  };

  const handleDelete = (id) => {
    setConfirmBox({ show: true, id });
  };

  // 👉 This actually deletes after clicking YES
  const confirmDelete = async () => {
    try {
      await axios.post(
        `${FRONTEND_CONFIG.API_URL}/api/question/delete-multiple`,
        { ids: [confirmBox.id] }
      );
      setQuestions((prev) => prev.filter((q) => q._id !== confirmBox.id));
      showAlert("🗑️ Question deleted successfully");
    } catch (error) {
      console.log(error);
      showAlert("❌ Failed to delete question");
    }
    setConfirmBox({ show: false, id: null });
  };

  const handleEditClick = (q) => {
    setEditingId(q._id);
    setEditedQuestion({ ...q });
  };

  const handleOptionChange = (index, value) => {
    const newOptions = [...editedQuestion.options];
    newOptions[index].optionText = value;
    setEditedQuestion((prev) => ({ ...prev, options: newOptions }));
  };

  const handleSave = async () => {
    try {
      await axios.put(
        `${FRONTEND_CONFIG.API_URL}/api/question/update-question/${editingId}`,
        editedQuestion
      );

      setQuestions((prev) =>
        prev.map((q) => (q._id === editingId ? editedQuestion : q))
      );

      setEditingId(null);
      showAlert("✅ Question updated successfully");
    } catch (err) {
      console.error("Failed to update:", err);
      showAlert("❌ Failed to update question");
    }
  };

  const handleCancel = () => setEditingId(null);

  return (
    <div className="min-h-screen bg-gray-100 py-6 px-4">

      {/* 🔥 Stylish Alert Popup */}
      {popup.show && (
        <div className="fixed inset-0 flex justify-center items-center bg-black/40 z-50">
          <div className="bg-white px-6 py-4 rounded-xl shadow-xl text-center">
            <p className="text-lg font-semibold text-gray-700">
              {popup.message}
            </p>
          </div>
        </div>
      )}

      {/* 🔥 NEW Stylish Delete Confirm Modal */}
      {confirmBox.show && (
        <div className="fixed inset-0 flex justify-center items-center bg-black/40 z-50">
          <div className="bg-white p-6 rounded-xl shadow-xl w-80 text-center">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">
              Are you sure you want to delete this question?
            </h2>

            <div className="flex justify-center gap-4">
              <button
                onClick={confirmDelete}
                className="bg-red-500 text-white px-4 py-2 rounded-lg hover:bg-red-600"
              >
                Yes
              </button>

              <button
                onClick={() => setConfirmBox({ show: false, id: null })}
                className="bg-gray-300 px-4 py-2 rounded-lg hover:bg-gray-400"
              >
                No
              </button>
            </div>
          </div>
        </div>
      )}

      <h1 className="text-2xl font-bold text-center text-blue-700 mb-6">
        🌟 Your Created Questions
      </h1>

      {questions.length === 0 ? (
        <p className="text-center text-gray-500">No questions found.</p>
      ) : (
        <div className="grid sm:grid-cols-1 lg:grid-cols-2 gap-4">
          {questions.map((q) => (
            <div
              key={q._id}
              className="bg-white shadow-sm rounded-lg p-4 border border-gray-200 relative"
            >
              {editingId === q._id ? (
                <>
                  <input
                    type="text"
                    value={editedQuestion.questionText}
                    onChange={(e) =>
                      setEditedQuestion((prev) => ({
                        ...prev,
                        questionText: e.target.value,
                      }))
                    }
                    className="w-full border px-2 py-1 rounded mb-2"
                  />

                  {editedQuestion.options.map((opt, index) => (
                    <div key={index} className="flex items-center gap-2 mb-1">
                      <span>{["A", "B", "C", "D"][index]}.</span>
                      <input
                        type="text"
                        value={opt.optionText}
                        onChange={(e) =>
                          handleOptionChange(index, e.target.value)
                        }
                        className="border px-2 py-1 rounded flex-1"
                      />
                      <input
                        type="radio"
                        name={`correct-${q._id}`}
                        checked={
                          editedQuestion.correctAnswer === opt.optionText
                        }
                        onChange={() =>
                          setEditedQuestion((prev) => ({
                            ...prev,
                            correctAnswer: opt.optionText,
                          }))
                        }
                        className="ml-2"
                      />
                    </div>
                  ))}

                  <input
                    type="text"
                    placeholder="Subject"
                    value={editedQuestion.subject}
                    onChange={(e) =>
                      setEditedQuestion((prev) => ({
                        ...prev,
                        subject: e.target.value,
                      }))
                    }
                    className="border px-2 py-1 rounded mb-1 w-full"
                  />

                  <input
                    type="text"
                    placeholder="Topic"
                    value={editedQuestion.topic || ""}
                    onChange={(e) =>
                      setEditedQuestion((prev) => ({
                        ...prev,
                        topic: e.target.value,
                      }))
                    }
                    className="border px-2 py-1 rounded mb-1 w-full"
                  />

                  <div className="flex justify-end gap-2 mt-3">
                    <button
                      onClick={handleSave}
                      className="bg-green-500 text-white px-3 py-1 rounded hover:bg-green-600"
                    >
                      💾 Save
                    </button>
                    <button
                      onClick={handleCancel}
                      className="bg-gray-300 px-3 py-1 rounded hover:bg-gray-400"
                    >
                      ❌ Cancel
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <h2 className="font-medium text-base mb-2 text-gray-900">
                    {q.questionText}
                  </h2>

                  <div className="space-y-1">
                    {q.options.map((opt, index) => {
                      const isCorrect =
                        q.correctAnswer === opt.optionText;
                      return (
                        <div
                          key={index}
                          className={`px-2 py-1 rounded-md border text-sm ${
                            isCorrect
                              ? "bg-green-100 border-green-500 text-green-700 font-semibold"
                              : "bg-gray-50 border-gray-300 text-gray-700"
                          }`}
                        >
                          {["A", "B", "C", "D"][index]}. {opt.optionText}
                        </div>
                      );
                    })}
                  </div>

                  <p className="text-xs text-gray-500 mt-2">
                    <strong>Subject:</strong> {q.subject}{" "}
                    {q.topic && (
                      <>
                        • <strong>Topic:</strong> {q.topic}
                      </>
                    )}
                  </p>

                  <div className="flex justify-end gap-3 mt-3">
                    <button
                      onClick={() => handleEditClick(q)}
                      className="text-blue-600 hover:text-blue-800 text-base"
                    >
                      ✏️
                    </button>

                    <button
                      onClick={() => handleDelete(q._id)}
                      className="text-red-600 hover:text-red-800 text-base"
                    >
                      🗑️
                    </button>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
